import { useState } from 'react';

import { Collapse } from '@components/animators/collapse';
import { EmptyState, EmptyStateCell } from '@components/empty-state';
import { IconMarketplace } from '@components/icons/marketplace';
import { IntegrationIcon } from '@components/integrations/common';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';

import { MarketplaceIntegration } from 'api/app-marketplace/requests';
import { useFeature } from 'ui/hooks';

import {
  ActionItem,
  ActionsList,
  GroupHeader,
  OnCreateActionSelectProps,
  SearchEmptyState,
  TabContentWrapper,
} from './common';

const collapsedAppsStore = createSelectionStore({
  persistKey: 'add-action-modal-collapsed-apps',
});

type AppsTabProps = {
  connectedApps: MarketplaceIntegration[] | undefined;
  notConnectedApps: MarketplaceIntegration[] | undefined;
  isLoading: boolean;
  onSelect: (props: OnCreateActionSelectProps) => void;
  searchQuery: string;
  onClearSearch: () => void;
};

export const AppsTab = ({
  connectedApps,
  notConnectedApps,
  isLoading,
  onSelect,
  searchQuery,
  onClearSearch,
}: AppsTabProps) => {
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasMarketplaceFeature } = useFeature('FEATURE_MARKETPLACE');

  const hasConnectedApps = connectedApps && connectedApps.length > 0;
  const hasNotConnectedApps = notConnectedApps && notConnectedApps.length > 0;
  const hasNoSearchResults = searchQuery && !hasConnectedApps && !hasNotConnectedApps;

  if (isLoading) {
    return (
      <TabContentWrapper>
        <Spinner />
      </TabContentWrapper>
    );
  }

  if (!hasMarketplaceFeature) {
    return (
      <TabContentWrapper>
        <EmptyState
          icon={<IconMarketplace />}
          header={t('title')}
          content={t('subtitle')}
          skipMargin
        />
      </TabContentWrapper>
    );
  }

  if (hasNoSearchResults) {
    return (
      <TabContentWrapper>
        <SearchEmptyState onClearSearch={onClearSearch} />
      </TabContentWrapper>
    );
  }

  return (
    <TabContentWrapper>
      <Stack space={4}>
        {hasConnectedApps ? (
          <Stack space={4}>
            <Text text={t('connectedApps')} font="semibold12" color="textSecondary" />
            {connectedApps.map((integration) => (
              <MarketplaceIntegrationGroup
                key={integration.shortId}
                integration={integration}
                onSelect={onSelect}
                isConnected={true}
              />
            ))}
          </Stack>
        ) : (
          <EmptyStateCell icon={<IconMarketplace />} text={t('noAppsConnected')} />
        )}

        <Stack space={4}>
          <Text text={t('availableApps')} font="semibold12" color="textSecondary" />
          {notConnectedApps?.map((integration) => (
            <MarketplaceIntegrationGroup
              key={integration.shortId}
              integration={integration}
              onSelect={onSelect}
              isConnected={false}
            />
          ))}
          {notConnectedApps?.length === 0 && (
            <EmptyStateCell icon={<IconMarketplace />} text={t('noMoreAvailableApps')} />
          )}
        </Stack>
      </Stack>
    </TabContentWrapper>
  );
};

type MarketplaceIntegrationGroupProps = {
  integration: MarketplaceIntegration;
  isConnected: boolean;
  onSelect: (props: OnCreateActionSelectProps) => void;
};

const MarketplaceIntegrationGroup = ({
  integration,
  isConnected,
  onSelect,
}: MarketplaceIntegrationGroupProps) => {
  const storedCollapsed = collapsedAppsStore.useStore((state) => state[integration.shortId]);
  const [localCollapsed, setLocalCollapsed] = useState(true);

  const isCollapsed = isConnected ? storedCollapsed : localCollapsed;
  const toggleCollapsed = isConnected
    ? () => collapsedAppsStore.toggle(integration.shortId)
    : () => setLocalCollapsed((prev) => !prev);

  return (
    <div>
      <GroupHeader
        onClick={toggleCollapsed}
        isCollapsed={isCollapsed}
        text={integration.name}
        textFont="semibold14"
        icon={<IntegrationIcon src={integration.logo} size="sm" />}
      />
      <Collapse isCollapsed={isCollapsed}>
        <ActionsList>
          {integration.endpoints.map((item) => (
            <MarketplaceActionItem
              key={item.shortId}
              item={item}
              variant="endpoint"
              isConnected={isConnected}
              onSelect={onSelect}
            />
          ))}
          {integration.webhooks.map((item) => (
            <MarketplaceActionItem
              key={item.shortId}
              item={item}
              variant="webhook"
              isConnected={isConnected}
              onSelect={onSelect}
            />
          ))}
        </ActionsList>
      </Collapse>
    </div>
  );
};

type MarketplaceActionItemProps = {
  item: { shortId: string; name: string; description?: string };
  variant: 'endpoint' | 'webhook';
  isConnected: boolean;
  onSelect: (props: OnCreateActionSelectProps) => void;
};

const marketplaceActionConfig = {
  endpoint: {
    type: 'integration' as const,
    actionType: 'integration' as const,
    connectedKey: 'integrationEndpointId',
    templateKey: 'templateEndpointId',
  },
  webhook: {
    type: 'webhook' as const,
    actionType: 'webhook' as const,
    connectedKey: 'integrationWebhookId',
    templateKey: 'templateWebhookId',
  },
};

const MarketplaceActionItem = ({
  item,
  variant,
  isConnected,
  onSelect,
}: MarketplaceActionItemProps) => {
  const config = marketplaceActionConfig[variant];

  return (
    <ActionItem
      text={item.name}
      description={item.description}
      type={config.type}
      onSelect={() =>
        onSelect({
          actionType: config.actionType,
          values: {
            [config.connectedKey]: { value: isConnected ? item.shortId : undefined },
            [config.templateKey]: !isConnected ? item.shortId : undefined,
          },
        })
      }
    />
  );
};

const locale = {
  en: {
    connectedApps: 'Connected apps',
    availableApps: 'Available apps',
    noAppsConnected: 'No apps connected',
    noMoreAvailableApps: 'No more available apps',
    title: 'App marketplace',
    subtitle:
      'Your integrations, one marketplace\n\nDiscover and connect tools that power your workflows — all in one place\n\nComing soon 🎉',
  },
};
